import { AuthService } from '../auth/auth.service';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { environment } from '../../environments/environment';

import moment from 'moment';
import { Observable } from 'rxjs';

export interface EgpResponses {
  egpresponses: Array<EgpResponse>;
}

export interface EgpResponse {
  matrices: Array<EgpMatrix>;
  stationIds: any;
  textObject: EgpTextObject;
}

export interface EgpMatrix {
  freezingRain: EgpMatrixDimensions;
  freezingWet: EgpMatrixDimensions;
  rime: EgpMatrixDimensions;
  snow: EgpMatrixDimensions;
}

export interface EgpMatrixDimensions {
  regionPercent: number;
  riskPercent: number;
}

export interface EgpTextObject {
  author: string;
  diffToPrevious: number;
  prescribeAt: string;
  texts: Array<EgpText>;
  writtenAt: string;
}

export interface EgpText {
  text: string;
  title: string;
  validFrom: string;
  validUntil: string;
}

export interface EgpRegionDef {
  description: string;
  id: string;
  name: string;
  station: {};
  superArea: {};
}

export interface SummerEgpRegionDef {
  egpareas: Array<EgpRegionDef>;
}

export interface DataWarning {
  warningResponses: [
    {
      message: string;
    },
  ];
}

export enum EgpSeason {
  SUMMER = 'summer',
  WINTER = 'winter',
}

export interface EgpRegion {
  id: string;
  name: string;
  season: EgpSeason;
}

export interface EgpRegionAndCountry {
  id: string;
  name: string;
  season: EgpSeason;
  country: string;
}

export interface EgpRegions {
  summer: Array<EgpRegionAndCountry>;
  winter: Array<EgpRegionAndCountry>;
}

@Injectable({
  providedIn: 'root',
})
export class EgpService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);
  private auth = inject(AuthService);

  private url = environment.url.bff + '/';
  warningMessage = {};

  private getTranslations(): void {
    for (const translationKey of ['AUTOENTWAR', 'NOWARN']) {
      this.warningMessage[translationKey] = this.translate.instant(
        'PRODUCT.WARNING.Active.' + translationKey
      );
    }
  }

  public getAllEgpAreas(
    season: EgpSeason,
    doReload = false
  ): Promise<Array<EgpRegionDef>> {
    return new Promise<Array<EgpRegionDef>>((resolve, reject) => {
      let egpAreas: Array<EgpRegionDef>;
      const storageKey = 'egp_regions_' + season;
      const storedRegions = localStorage.getItem(storageKey);
      if (storedRegions && !doReload) {
        egpAreas = JSON.parse(storedRegions);
        resolve(egpAreas);
      } else {
        this.getAllEgpAreasFromBff(season).subscribe(
          data => {
            egpAreas = season === EgpSeason.WINTER ? data : data['egpareas'];
          },
          err => {
            reject(err);
          },
          () => {
            localStorage.setItem(storageKey, JSON.stringify(egpAreas));
            resolve(egpAreas);
          }
        );
      }
    });
  }

  public getAllEgpAreasFromBff(
    season: EgpSeason
  ): Observable<Array<EgpRegionDef> | SummerEgpRegionDef> {
    const fullUrl =
      environment.url.bff +
      '/egp/egp' +
      (season === EgpSeason.SUMMER ? 'summer' : '') +
      'area/list';
    return this.http.get<Array<EgpRegionDef> | SummerEgpRegionDef>(fullUrl);
  }

  public getEgp(areaId: string): Promise<Array<EgpResponse>> {
    return new Promise((resolve, reject) => {
      this.getEgpFromBFF(areaId).subscribe(
        data => {
          if (data.egpresponses.length === 0) {
            reject({
              message: 'Es wurden keine EGPs gefunden',
              status: 'Error',
              url: 'getEgpFromBFF',
            });
          }
          // array of 15 egps  find the one which is actual at this time
          let currentEgp: EgpResponse;
          let previousEgp: EgpResponse;
          for (const egp of data.egpresponses) {
            const egpTime = moment(egp.textObject.prescribeAt);
            if (egpTime > moment()) {
              continue;
            }
            if (!currentEgp) {
              currentEgp = egp;
              continue;
            }
            if (!previousEgp) {
              previousEgp = egp;
              resolve([currentEgp, previousEgp]);
            }
          }
          resolve([currentEgp, previousEgp]);
        },
        error1 => {
          reject(error1);
        }
      );
    });
  }

  private getEgpFromBFF(areaId: string): Observable<EgpResponses> {
    return this.http.get<EgpResponses>(
      environment.url.bff + '/egp/' + areaId + '/15'
    );
  }

  private getEgpRegionIdsOfCustomerDocument(
    customerDocument: any,
    season: string,
    alsoOutOfSeason?: boolean,
    isUserAdmin?: boolean
  ): Array<string> {
    const productsKeysOfSeason = {
      winter: ['egpProducts', 'egwProducts'],
      summer: ['egpSummerProducts'],
    };
    const regionIds = [];
    for (const productKey of productsKeysOfSeason[season]) {
      for (const product of customerDocument[productKey]) {
        if (
          this.auth.isProductActive(
            productKey,
            product.id,
            false,
            alsoOutOfSeason,
            isUserAdmin
          )
        ) {
          if (regionIds.indexOf(product.areaId) < 0) {
            regionIds.push(product.areaId + '');
          }
        }
      }
    }
    return regionIds;
  }

  public getEgpRegionsOfCustomer(
    customerDocument: any,
    season: EgpSeason,
    alsoOutOfSeason?: boolean,
    isUserAdmin?: boolean
  ): Promise<Array<object>> {
    return new Promise(resolve => {
      const storageKey = 'customerEgpRegions' + season;
      const customerEgpRegionsFromCache = sessionStorage.getItem(storageKey);
      let customerEgpRegions;
      if (customerEgpRegionsFromCache) {
        customerEgpRegions = JSON.parse(customerEgpRegionsFromCache);
        if (customerEgpRegions.customerId !== customerDocument.customerId) {
          sessionStorage.removeItem('customerEgpRegions');
        } else if (customerEgpRegions.regions.length > 0) {
          resolve(customerEgpRegions.regions);
        }
      }
      if (!customerEgpRegions) {
        const orderedEgpRegionIds = this.getEgpRegionIdsOfCustomerDocument(
          customerDocument,
          season,
          alsoOutOfSeason,
          isUserAdmin
        );
        return this.getAllEgpAreas(season).then(egpAreas => {
          const regions = [];
          for (const dataset of egpAreas) {
            const regionId = dataset.id;
            if (
              customerDocument.isMaster ||
              orderedEgpRegionIds.indexOf(regionId) >= 0
            ) {
              const region = {
                areaId: regionId,
                name: dataset.name,
                station: dataset.station,
              };
              regions.push(region);
            }
          }
          resolve(regions);
        });
      }
    });
  }

  public getPreviousEgpOfDeadline(areaId, deadline: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getEgpFromBFF(areaId).subscribe(
        data => {
          // array of 15 egps  find the one which is actual at this time
          for (const egp of data['egpresponses']) {
            const egpTime = moment(egp.textObject.prescribeAt);
            if (egpTime > moment().subtract(1, 'day')) {
              continue;
            }
            if (egpTime.format('HH:mm') !== deadline) {
              continue;
            }
            resolve(egp);
            return;
          }
          resolve(null);
        },
        error => reject(error)
      );
    });
  }

  getRiskBoxValues(matrices): Array<any> {
    const riskBoxes: Array<any> = [];
    for (const matrix of matrices) {
      const riskBox = {
        EIS: {
          area: matrix['freezingRain']['regionPercent'],
          risk: matrix['freezingRain']['riskPercent'],
        },
        SCHNEE: {
          area: matrix['snow']['regionPercent'],
          risk: matrix['snow']['riskPercent'],
        },
        REIF: {
          area: matrix['rime']['regionPercent'],
          risk: matrix['rime']['riskPercent'],
        },
        NAESSE: {
          area: matrix['freezingWet']['regionPercent'],
          risk: matrix['freezingWet']['riskPercent'],
        },
      };
      riskBoxes.push(riskBox);
    }
    return riskBoxes;
  }

  calcArea(metmanValue: number): number {
    // 0 10 30 70 100
    const mapping = {
      0: 0,
      15: 0.25,
      40: 0.5,
      70: 0.75,
      100: 1,
    };
    return mapping[metmanValue];
  }

  calcRisk(metmanValue: number): number {
    // 0 15 40 70 97
    const mapping = {
      0: 0,
      15: 0.15,
      40: 0.4,
      70: 0.7,
      95: 0.95,
    };
    return mapping[metmanValue];
  }

  formatTexts(texts: any): Array<string> {
    const formattedTexts = [];
    for (const unformatted of texts) {
      const paragraphs = unformatted.text.split('\n');
      const completeParagraphs = [];
      for (const paragraph of paragraphs) {
        if (paragraph === '') {
          continue;
        }
        completeParagraphs.push(paragraph);
      }
      formattedTexts.push(completeParagraphs);
    }
    return formattedTexts;
  }
}
